'use client'

import { useCallback } from 'react'

import { styled } from 'styled-components'

import { useAuthenticatedPerson } from '@b-stock/fe-core/bstock-next/components'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  Link,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'

import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

// Styled Components
const BannerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: ${Colors.Semantic.BStock.default.lightBackground};
`

const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: left;
  text-align: left;
  width: 100%;
  max-width: var(--maxPageWidth);
  margin: 0 auto;

  @media ${Breakpoints.max.medium} {
    max-width: 43.625rem;
  }
`

const Headline = styled.h1`
  ${Typography.Title1}
  color: ${Colors.Semantic.Neutral.white};
  margin-bottom: 1rem;
`

const Subheadline = styled.p`
  color: ${Colors.Semantic.Neutral.white};
  ${Typography.Body2}
`

const ShopInventoryLink = styled(Link)`
  color: ${Colors.Semantic.Neutral.white};
  font-weight: 700;
  text-decoration: underline;
  transition: color 0.2s ease;

  &:hover {
    color: ${Colors.Semantic.Warning.onhover};
  }

  &:focus {
    outline: 0.125rem solid ${Colors.Semantic.Info.highlight};
    outline-offset: 0.125rem;
  }
`

/**
 * PersonalizedWelcomeBanner Component
 *
 * Displays a personalized welcome message to logged-in users with a call-to-action
 * to browse inventory. Part of the A/B test to replace rotating marketing carousel.
 */
export default function PersonalizedWelcomeBanner() {
  const { trackButtonClicked } = useAnalytics()

  const person = useAuthenticatedPerson()
  const firstName = person?.firstName
  const lastName = person?.lastName

  // Build personalized headline with edge case handling
  const getHeadline = (): string => {
    if (firstName && lastName) {
      return `Welcome back, ${firstName} ${lastName}`
    }
    if (firstName) {
      return `Welcome back, ${firstName}`
    }
    if (lastName) {
      return `Welcome back, ${lastName}`
    }
    return 'Welcome!'
  }

  // Handle analytics tracking for Shop Inventory link click
  const handleShopInventoryClick = useCallback(
    (href: string) => {
      trackButtonClicked({
        screenName: 'homepage',
        buttonName: 'welcome_banner_cta',
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
        ab_test_name: 'logged_in_homepage_welcome_banner',
        ab_test_variant: 'treatment',
        target_url: href,
        url: document.URL,
        referrer: document.referrer,
      })
    },
    [trackButtonClicked]
  )

  return (
    <BannerWrapper>
      <BannerContent>
        <Headline aria-level={1}>{getHeadline()}</Headline>
        <Subheadline>
          Ready to find your next great deal?
          <br />
          <ShopInventoryLink
            href="/all-auctions"
            onClick={() => handleShopInventoryClick('/all-auctions')}
            aria-label="Shop Inventory across all our Branded Sellers"
          >
            Shop Inventory
          </ShopInventoryLink>{' '}
          across all our Branded Sellers
        </Subheadline>
      </BannerContent>
    </BannerWrapper>
  )
}
